import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import { getExtraTimeRequirementForUser } from '@/helpers/userHelper';
import { useAppSelector } from '@/hooks';
import { useHandlePopstate } from '@/hooks/useHandlePopState';
import { BookState, ExtraTimeRequirement } from '@/types/state/book';
import { Place, PlaceEmployee, PlaceService } from '@/types/state/shared';
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  handleGoBack,
  handleOnPickEmployee,
  handleOnPickService,
  handleOnRemoveService,
  handleOnPickExtraTime,
} from './BookAppointment.handlers';
import {
  getPickedEmployee,
  getScreenView,
  getUrlParameters,
  setUrlState,
  syncBookstateWithUrl,
} from './BookAppointment.helpers';
import { ModalView, View } from './BookAppointment.types';

/**
 * Configuration object to disable default behaviors
 */
export type Config = {
  disableTracking?: boolean;
  disableViewUpdate?: boolean;
  disableUrlUpdate?: boolean;
  /** Booking-flow-redesign variant: don't sync bookstate with the URL (no modal auto-open). */
  disableUrlSync?: boolean;
  /**
   * `false` while the disableUrlSync decision is still pending (the redesign flag resolves
   * async): URL syncs wait instead of latching the control default. Omitted/true = decided.
   */
  urlSyncReady?: boolean;
};

const useUpdateUrlState = (place?: Place) => {
  const history = useHistory();
  const location = useLocation();

  return useCallback(
    (
      updated:
        | { type: 'employee'; externalId: string }
        | { type: 'mainServices'; serviceIds: number[] }
        | { type: 'addOnServices'; serviceIds: number[] },
    ) => {
      if (!place) return;

      switch (updated.type) {
        case 'employee':
          setUrlState({ sr: updated.externalId ?? null }, history, location);
          break;
        case 'mainServices':
          setUrlState({ ss: updated.serviceIds?.join(',') ?? null }, history, location);
          break;
        case 'addOnServices':
          setUrlState({ rs: updated.serviceIds?.join(',') ?? null }, history, location);
          break;
      }
    },
    [place, history, location],
  );
};

const useBookAppointmentManager = (
  place?: Place,
  config?: Config,
): {
  view: ModalView;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  updateView: (view: View) => void;
  updateUrlState: (
    updated:
      | { type: 'employee'; externalId: string }
      | { type: 'mainServices'; serviceIds: number[] }
      | { type: 'addOnServices'; serviceIds: number[] },
  ) => void;
  onPickEmployee: (employee: PlaceEmployee) => void;
  onPickService: (service: PlaceService, isRecommended: boolean) => void;
  onRemoveService: (serviceId: number) => void;
  onPickExtraTime: (extraTime: ExtraTimeRequirement) => void;
  onGoBack: (view: View) => void;
} => {
  /**
   * Initial screen depends on if the user has picked a service or an employee
   * - if the user picks a service, and there is more than one employee && the place does not have hideEmployee setting, show the pickEmployee screen
   * - if the user picks a employee, show the pickServices screen
   */
  const bookstate = useAppSelector((state) => state.book) as BookState;
  const { user } = useAppSelector((state) => state.users);
  const extraTimeRequiredForUser = getExtraTimeRequirementForUser(user, place?.id);
  const [view, setView] = useState<ModalView>(getScreenView(bookstate, place, undefined, extraTimeRequiredForUser));
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const mountedRef = useRef<boolean>(false);
  const hasSyncedUrlRef = useRef<boolean>(false);
  const popstate = useHandlePopstate();
  const navigation = useLocation();
  const updateUrlState = useUpdateUrlState(place);

  const updateView = useCallback((next: View) => {
    setView((prev) => ({ current: next, previous: prev.current }));
  }, []);

  const open = useCallback(() => {
    if (view.current === undefined) {
      setView(getScreenView(bookstate, place, view.current, extraTimeRequiredForUser));
    }
    setIsOpen(true);
  }, [bookstate, place, view, extraTimeRequiredForUser]);

  const close = () => {
    setIsOpen(false);
    if (!mountedRef.current) return;
    setUrlState({ sr: null, ss: null, rs: null }, history, navigation);
  };

  const ctx = { view, close, updateView, updateUrlState };

  const onPickEmployee = useCallback(
    (employee: PlaceEmployee) => {
      return handleOnPickEmployee(ctx, dispatch, config)(employee);
    },
    [bookstate, dispatch, updateUrlState, updateView],
  );

  const onPickService = useCallback(
    (service: PlaceService, isRecommended: boolean) => {
      return handleOnPickService(ctx, bookstate, dispatch, config)(service, isRecommended);
    },
    [bookstate, dispatch, updateUrlState, updateView],
  );

  const onGoBack = useCallback(
    (from: View) => {
      return handleGoBack(ctx, bookstate, dispatch)(from);
    },
    [view, close, updateView, updateUrlState, bookstate, dispatch],
  );

  const onRemoveService = useCallback(handleOnRemoveService(ctx, bookstate, dispatch, config), [
    view,
    close,
    updateView,
    updateUrlState,
    bookstate,
    dispatch,
  ]);

  const onPickExtraTime = useCallback(
    (extraTime: ExtraTimeRequirement) => {
      return handleOnPickExtraTime(ctx, bookstate, dispatch, config, user)(extraTime);
    },
    [bookstate, dispatch, updateUrlState, updateView, user],
  );

  useEffect(() => {
    if (view.current === undefined) {
      setView(getScreenView(bookstate, place, view.current, extraTimeRequiredForUser));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookstate.services, bookstate.employee, view.current]);

  useEffect(
    () => {
      if (!place || config?.urlSyncReady === false || config?.disableUrlSync) return;
      /**
       * If the url has been updated by the browser navigation
       * change the input values to match the url and perform a new search
       * to sync the search results with the url
       */
      let shouldSyncState = false;
      const selectedEmployee = getPickedEmployee(bookstate.employee, place?.employees || [])?.about?.externalId;
      const selectedServices = (bookstate.services || [])?.map((s) => s.id).join(',');
      const selectedRecommendedServices = (bookstate.services || [])
        .filter((s) => s?.about?.settings?.isAddOn)
        .map((s) => s.id)
        .join(',');

      const urlState = getUrlParameters();

      if (popstate.didUpdate) {
        if (selectedEmployee !== urlState.sr) {
          shouldSyncState = true;
        }

        if (selectedServices !== urlState.ss) {
          shouldSyncState = true;
        }

        if (selectedRecommendedServices !== urlState.rs) {
          shouldSyncState = true;
        }

        if (shouldSyncState) {
          syncBookstateWithUrl(urlState, bookstate, place, dispatch, { open, close, view, updateView });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [popstate.didUpdate, navigation],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(
    () => {
      // One-shot sync, latched only when acted on: a variant decision consumes the shot
      // without syncing, and an undecided or place-less render leaves it unconsumed so a
      // late-resolving flag can still block a sync that has not happened yet. A modal the
      // visitor opened during the wait consumes it too: the sync would pop and re-add the
      // selection and force the view from the URL, moving them off the screen they are on.
      if (hasSyncedUrlRef.current || config?.urlSyncReady === false) return;
      if (config?.disableUrlSync || isOpen) {
        hasSyncedUrlRef.current = true;
        return;
      }
      if (!place) return;
      hasSyncedUrlRef.current = true;
      syncBookstateWithUrl(getUrlParameters(), bookstate, place, dispatch, { open, close, view, updateView });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.urlSyncReady, config?.disableUrlSync, isOpen],
  );

  return {
    view,
    isOpen,
    open,
    close,
    updateView,
    updateUrlState,
    onPickEmployee,
    onPickService,
    onRemoveService,
    onGoBack,
    onPickExtraTime,
  };
};

export type BookAppointmentContext = ReturnType<typeof useBookAppointmentManager>;

export const BookAppointmentContext = createContext<BookAppointmentContext>({
  view: { current: 'summary' },
  isOpen: false,
  open: () => null,
  close: () => null,
  updateView: () => null,
  updateUrlState: () => null,
  onPickEmployee: () => null,
  onPickService: () => null,
  onRemoveService: () => null,
  onGoBack: () => null,
  onPickExtraTime: () => null,
});

export const useBookAppointmentContext = () => {
  const context = useContext(BookAppointmentContext);

  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }

  return context;
};

export const BookAppointmentProvider: React.FC<{ children: ReactNode; place?: Place; config?: Config }> = ({
  children,
  place,
  config,
}) => {
  const { show, loadSource } = useAppSelector((state) => state.loading);

  const manager = useBookAppointmentManager(place, config);

  return (
    <BookAppointmentContext.Provider value={manager}>
      {show && loadSource === 'fetchPlaceDetails' && (
        <div className="fixed inset-0 z-10">
          <LoadingPlaceHolder className="flex items-center justify-center" />
        </div>
      )}
      {children}
    </BookAppointmentContext.Provider>
  );
};
